import * as React from 'react';
import { DayPicker, type DayButtonProps } from 'react-day-picker';
import { format, isToday } from 'date-fns';
import { cn } from '../../utils';
import { CalendarDayButton } from '../core/calendar';
import { Button } from '../core/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../core/dialog';

/**
 * A single entry rendered on the shared event calendar. Times are ISO UTC
 * strings; display formatting converts to the viewer's local timezone.
 */
export type EventCalendarEvent = {
  eventId: string;
  title: string;
  startTime: string;
  endTime: string;
  eventKind: string;
  status: 'scheduled' | 'cancelled' | 'completed' | 'draft';
  isTemplate?: boolean;
  seriesTemplateId?: string | null;
  category?: {
    eventCategoryId: string;
    name: string;
    slug?: string | null;
  } | null;
};

/**
 * All user-facing strings the calendar renders. Consumers translate with
 * their own i18n namespace so the component stays namespace-agnostic.
 */
export type EventCalendarLabels = {
  /** "+{count} more" link when a day has more events than fit in the cell. */
  more: (count: number) => string;
  /** "{count} events" line in the day dialog header. */
  dayEventsCount: (count: number) => string;
  noEvents: string;
  noEventsForDay: string;
  /** Label for the create button; only relevant when `onCreateEvent` is set. */
  addEvent?: string;
  /** Human label for an Event.eventKind value. */
  kind: (kind: string) => string;
};

type EventCalendarContextValue = {
  eventsByDate: Map<string, EventCalendarEvent[]>;
  labels: EventCalendarLabels;
  onSelectEvent?: (eventId: string) => void;
  onCreateEvent?: (date: Date) => void;
  renderEventAction?: (event: EventCalendarEvent) => React.ReactNode;
  openDateKey: string | null;
  setOpenDateKey: (key: string | null) => void;
  selectDate: (date: Date) => void;
};

/** True for events that happen today and have not ended yet. */
const isUpcomingToday = (event: EventCalendarEvent) =>
  isToday(new Date(event.startTime)) && new Date(event.endTime).getTime() >= Date.now();

const EventCalendarContext = React.createContext<EventCalendarContextValue | null>(null);

const formatDateKey = (date: Date) => format(date, 'yyyy-MM-dd');
const formatTime = (iso: string) => format(new Date(iso), 'HH:mm');
const ensureMapArray = <T,>(map: Map<string, T[]>, key: string) => map.get(key) ?? (map.set(key, []), map.get(key)!);

const CALENDAR_CLASS_NAMES = {
  nav: 'flex w-full items-center justify-between px-2 pb-3',
  month: 'relative flex w-full flex-col gap-4 pt-12',
  month_caption: 'absolute left-1/2 top-0 flex -translate-x-1/2 items-center gap-2',
  caption_label: 'hidden',
  dropdowns: 'flex items-center gap-2 text-sm font-medium',
  // Fluid grid: equal-width columns that shrink with the viewport instead of
  // sizing to their content (which pushed the last columns off narrow screens).
  month_grid: 'w-full table-fixed border-collapse',
  day: 'p-0.5 align-top',
} as const;

function EventCalendarDayButton(props: DayButtonProps) {
  const context = React.useContext(EventCalendarContext);
  const dateKey = formatDateKey(props.day.date);
  const events = context?.eventsByDate.get(dateKey) ?? [];
  const hasEvents = events.length > 0;
  const isOpen = context?.openDateKey === dateKey;
  // Opening always routes through selectDate so the single-event shortcut
  // (open the event directly instead of the day list) applies on every path.
  const setDialogOpen = React.useCallback(
    (open: boolean) => {
      if (open) {
        context?.selectDate(props.day.date);
      } else {
        context?.setOpenDateKey(null);
      }
    },
    [context, props.day.date],
  );

  // Only close the dialog if events were removed while it was open
  // (e.g., all events on a day were deleted)
  const prevHasEventsRef = React.useRef(hasEvents);
  React.useEffect(() => {
    if (prevHasEventsRef.current && !hasEvents && isOpen) {
      setDialogOpen(false);
    }
    prevHasEventsRef.current = hasEvents;
  }, [hasEvents, isOpen, setDialogOpen]);

  const selectEvent = (eventId: string) => {
    context?.onSelectEvent?.(eventId);
  };

  const triggerEventSelect = (eventId: string, options: { closePopover?: boolean } = {}) => {
    if (options.closePopover !== false) {
      setDialogOpen(false);
    }
    selectEvent(eventId);
  };

  const handleEventSelect = (
    eventId: string,
    options: { closePopover?: boolean } = {},
  ) => (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    triggerEventSelect(eventId, options);
  };

  const handleCreateEvent = () => {
    setDialogOpen(false);
    context?.onCreateEvent?.(props.day.date);
  };

  const handleShowMore = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDialogOpen(true);
  };

  const isCurrentDay = isToday(props.day.date);

  const button = (
    <CalendarDayButton
      {...props}
      className={cn(
        'relative items-start justify-start gap-1 px-1 py-1 text-left min-h-[80px]',
        hasEvents
          ? 'data-[range-middle=true]:rounded-md border border-primary/40 bg-primary/5'
          : 'bg-muted text-foreground hover:bg-muted/80',
        // Standard "today" cue (shadcn calendar convention): accented cell ring.
        // Full-strength ring + tinted background so today reads at a glance.
        isCurrentDay && 'ring-2 ring-inset ring-primary bg-primary/10',
      )}
      onClick={(event) => {
        props.onClick?.(event);
        if (!hasEvents && context?.onCreateEvent) {
          event.preventDefault();
          event.stopPropagation();
          handleCreateEvent();
        }
      }}
    >
      <span
        className={cn(
          'text-xs font-semibold leading-none',
          isCurrentDay
            ? 'flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-primary-foreground'
            : 'text-foreground',
        )}
      >
        {format(props.day.date, 'd')}
      </span>
      {hasEvents && (
        <div className="absolute inset-x-1 bottom-1 flex flex-col gap-0.5 text-left text-[10px] leading-tight max-h-[60px] overflow-hidden">
          {events.slice(0, 2).map((event) => (
            <div
              key={event.eventId}
              role="button"
              tabIndex={0}
              onClick={handleEventSelect(event.eventId, { closePopover: false })}
              onKeyDown={(eventKey) => {
                if (eventKey.key === 'Enter' || eventKey.key === ' ') {
                  eventKey.preventDefault();
                  eventKey.stopPropagation();
                  triggerEventSelect(event.eventId, { closePopover: false });
                }
              }}
              className={cn(
                'w-full truncate rounded-sm px-1 text-left text-[10px] leading-4 transition focus:outline-none focus:ring-2',
                // Today's still-upcoming events pop in the design system's red;
                // past-today and other-day events keep the default styling.
                isUpcomingToday(event)
                  ? 'bg-destructive/10 font-semibold text-destructive hover:bg-destructive/20 focus:ring-destructive/40'
                  : 'bg-primary/10 text-primary hover:bg-primary/20 focus:ring-primary/40',
              )}
              title={`${formatTime(event.startTime)} · ${event.title}`}
            >
              {formatTime(event.startTime)} · {event.title}
            </div>
          ))}
          {events.length > 2 && context && (
            <div
              role="button"
              tabIndex={0}
              onClick={handleShowMore}
              onKeyDown={(eventKey) => {
                if (eventKey.key === 'Enter' || eventKey.key === ' ') {
                  eventKey.preventDefault();
                  setDialogOpen(true);
                }
              }}
              className="text-left text-[10px] text-muted-foreground underline focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {context.labels.more(events.length - 2)}
            </div>
          )}
        </div>
      )}
    </CalendarDayButton>
  );

  if (!context) {
    return button;
  }

  const { labels } = context;

  return (
    <Dialog open={Boolean(isOpen)} onOpenChange={(open) => setDialogOpen(open)}>
      <DialogTrigger asChild>{button}</DialogTrigger>
      <DialogContent className="w-full max-w-md space-y-4">
        <DialogHeader>
          <DialogTitle>{format(props.day.date, 'EEEE, MMM d')}</DialogTitle>
          <DialogDescription>
            {events.length ? labels.dayEventsCount(events.length) : labels.noEvents}
          </DialogDescription>
        </DialogHeader>
        {events.length ? (
          <div className="space-y-3">
            {events.map((event) => {
              const { eventId, startTime, endTime, eventKind, title, category, status } = event;
              const start = formatTime(startTime);
              const end = formatTime(endTime);
              const action = context.renderEventAction?.(event);
              return (
                <div
                  key={eventId}
                  className={cn(
                    'flex items-start gap-4 rounded-md border border-border px-3 py-3 text-sm transition hover:border-primary hover:bg-primary/5',
                    status !== 'scheduled' && 'opacity-75',
                  )}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    className="flex min-w-0 flex-1 items-start gap-4 text-left hover:bg-transparent p-0"
                    onClick={handleEventSelect(eventId)}
                  >
                    {/* Left side: eventKind, start/end time */}
                    <div className="flex min-w-[80px] flex-col items-start gap-1 text-left">
                      <span className="rounded bg-muted px-2 py-0.5 text-xs capitalize text-muted-foreground">
                        {labels.kind(eventKind)}
                      </span>
                      <span className="text-xs font-medium text-foreground">
                        {start} – {end}
                      </span>
                    </div>

                    {/* Right side: category and title */}
                    <div className="flex min-w-0 flex-1 flex-col items-start justify-start gap-1 text-left">
                      {category?.name && (
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">
                          {category.name}
                        </p>
                      )}
                      <p className="text-xs font-medium text-foreground">{title}</p>
                    </div>
                  </Button>

                  {/* Optional trailing slot (e.g. admin delete button) */}
                  {action ? <div className="flex-shrink-0">{action}</div> : null}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{labels.noEventsForDay}</p>
        )}
        {context.onCreateEvent && labels.addEvent && (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={handleCreateEvent}>
              {labels.addEvent}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export type EventCalendarProps = {
  month: Date;
  onMonthChange?: (date: Date) => void;
  events?: EventCalendarEvent[];
  labels: EventCalendarLabels;
  /** Called with the eventId when the user clicks an event. */
  onSelectEvent?: (eventId: string) => void;
  /** When provided, empty days become create targets and the day dialog shows an add button. */
  onCreateEvent?: (date: Date) => void;
  /** Trailing slot rendered next to each event row in the day dialog. */
  renderEventAction?: (event: EventCalendarEvent) => React.ReactNode;
  /**
   * When a clicked day has exactly one event, call `onSelectEvent` with it
   * directly instead of opening the day-list dialog. Days with 2+ events
   * still show the list. Off by default so consumers whose day list carries
   * extra affordances (e.g. admin's delete button) keep the intermediate step.
   */
  openSingleEventDirectly?: boolean;
  className?: string;
};

/**
 * Shared month calendar for events. Presentational and read-only by default:
 * consumers supply the events, labels and click handlers. Admin layers create
 * and delete affordances on top via `onCreateEvent` / `renderEventAction`.
 */
export function EventCalendar({
  month,
  onMonthChange,
  events = [],
  labels,
  onSelectEvent,
  onCreateEvent,
  renderEventAction,
  openSingleEventDirectly = false,
  className,
}: EventCalendarProps) {
  const [selectedDay, setSelectedDay] = React.useState<Date>();
  const eventsByDate = React.useMemo(() => {
    // Dedupe defensively: drop repeated ids and collapse distinct rows that
    // would render identically (same trimmed title + same start/end minute).
    // Happens e.g. when the same session is materialized once per linked
    // group and the viewer belongs to more than one of them.
    const seenIds = new Set<string>();
    const seenSignatures = new Set<string>();
    const minuteKey = (iso: string) => {
      const ms = Date.parse(iso);
      return Number.isFinite(ms) ? Math.floor(ms / 60_000) : iso;
    };
    const deduped = events.filter((event) => {
      const signature = `${event.title.trim()}|${minuteKey(event.startTime)}|${minuteKey(event.endTime)}`;
      if (seenIds.has(event.eventId) || seenSignatures.has(signature)) return false;
      seenIds.add(event.eventId);
      seenSignatures.add(signature);
      return true;
    });

    const map = deduped.reduce((acc, event) => {
      ensureMapArray(acc, formatDateKey(new Date(event.startTime))).push(event);
      return acc;
    }, new Map<string, EventCalendarEvent[]>());
    map.forEach((list) =>
      list.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()),
    );
    return map;
  }, [events]);
  // Note: empty days are allowed to keep their dialog open (it shows the
  // "no events" copy). Days whose events disappear while the dialog is open
  // are closed by the effect inside EventCalendarDayButton.
  const [openDateKey, setOpenDateKey] = React.useState<string | null>(null);
  const handleSelectDate = React.useCallback(
    (date?: Date | null) => {
      const nextDate = date ?? selectedDay;
      if (!nextDate) return;
      setSelectedDay(nextDate);
      const dateKey = formatDateKey(nextDate);
      // Single-event days skip the intermediate list and open the event itself.
      const dayEvents = eventsByDate.get(dateKey) ?? [];
      if (openSingleEventDirectly && dayEvents.length === 1 && onSelectEvent) {
        onSelectEvent(dayEvents[0].eventId);
        return;
      }
      setOpenDateKey(dateKey);
    },
    [selectedDay, eventsByDate, openSingleEventDirectly, onSelectEvent],
  );
  const contextValue = React.useMemo<EventCalendarContextValue>(
    () => ({
      eventsByDate,
      labels,
      onSelectEvent,
      onCreateEvent,
      renderEventAction,
      openDateKey,
      setOpenDateKey,
      selectDate: (date: Date) => handleSelectDate(date),
    }),
    [eventsByDate, labels, onSelectEvent, onCreateEvent, renderEventAction, openDateKey, handleSelectDate],
  );

  return (
    <EventCalendarContext.Provider value={contextValue}>
      <DayPicker
        mode="single"
        month={month}
        onMonthChange={onMonthChange}
        selected={selectedDay}
        onSelect={handleSelectDate}
        showOutsideDays
        captionLayout="dropdown"
        modifiers={{ hasEvents: (date: Date) => eventsByDate.has(formatDateKey(date)) }}
        modifiersClassNames={{
          hasEvents: 'data-[selected=false]:bg-muted data-[selected=false]:text-foreground',
        }}
        startMonth={new Date(2020, 0)}
        endMonth={new Date(2030, 11)}
        className={cn('bg-background group/calendar rounded-md border p-3 shadow-sm', className)}
        classNames={CALENDAR_CLASS_NAMES}
        components={{
          DayButton: EventCalendarDayButton,
        }}
      />
    </EventCalendarContext.Provider>
  );
}
